import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';
import { Amplify } from 'aws-amplify';
import { installSafeAmplifyFetch } from '@jepto/shared/utils/safe-fetch.util';
import { log } from '@jepto/shared/utils/logger';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { reportAnalyticsError } from './app/utils/analytics-error-reporting.util';
import {
	applySentryBeforeSend,
	SENTRY_DENY_URLS,
} from '@jepto/shared/utils/sentry-before-send.util';
import {
	readBrowserAutomationSignals,
	shouldEnableSentryReplay,
} from '@jepto/shared/utils/sentry-replay-guard.util';
import { environment } from './environments/environment';

const sentryConfig = environment.sentry;

const tracePropagationTargets = [
	'localhost',
	environment.appSyncConfig.customEndpoint,
	environment.appSyncConfig.eventsEndpoint,
].filter(Boolean);

const sentryEnvironment = environment.production
	? 'production'
	: environment.staging
		? 'staging'
		: 'development';

// Do not send events from localhost (local dev) to avoid polluting Sentry
const isLocalhost =
	typeof window !== 'undefined' &&
	(window.location.hostname === 'localhost' ||
		window.location.hostname === '127.0.0.1');

if (isLocalhost) {
	if (!environment.production) {
		log('[Analytics][Sentry] Init skipped: localhost guard', {
			hostname:
				typeof window !== 'undefined'
					? window.location.hostname
					: 'unknown',
		});
	}
} else if (!sentryConfig?.enabled) {
	if (!environment.production) {
		log('[Analytics][Sentry] Init skipped: sentry.enabled is false');
	}
} else if (!sentryConfig?.dsn) {
	if (!environment.production) {
		log('[Analytics][Sentry] Init skipped: missing DSN');
	}
} else {
	const enableReplay = shouldEnableSentryReplay(
		readBrowserAutomationSignals()
	);
	if (!environment.production) {
		log('[Analytics][Sentry] Initializing client', {
			environment: sentryEnvironment,
			debug: sentryConfig.debug ?? false,
			enableReplay,
		});
	}
	Sentry.init({
		dsn: sentryConfig.dsn,
		release: environment.sentryRelease,
		sendDefaultPii: true,
		denyUrls: [...SENTRY_DENY_URLS],
		beforeSend(event, hint) {
			try {
				return applySentryBeforeSend(event, hint);
			} catch {
				return event;
			}
		},
		integrations: [
			Sentry.browserTracingIntegration(),
			...(enableReplay
				? [
						Sentry.replayIntegration({
							maskAllText: false,
							maskAllInputs: false,
							mask: ['input[type="password"]'],
							blockAllMedia: false,
							networkDetailAllowUrls: tracePropagationTargets,
							networkCaptureBodies: true,
						}),
					]
				: []),
		],
		tracesSampleRate: sentryConfig.tracesSampleRate,
		tracePropagationTargets,
		replaysSessionSampleRate: enableReplay
			? sentryConfig.replaysSessionSampleRate
			: 0,
		replaysOnErrorSampleRate: enableReplay
			? sentryConfig.replaysOnErrorSampleRate
			: 0,
		enableLogs: true,
		environment: sentryEnvironment,
		debug: sentryConfig.debug ?? false,
	});
	if (!environment.production) {
		log('[Analytics][Sentry] Client initialized', {
			hasClient:
				typeof Sentry.getClient === 'function' && !!Sentry.getClient(),
		});
	}
}

installSafeAmplifyFetch();

Amplify.configure({
	API: {
		GraphQL: {
			endpoint: environment.appSyncConfig.customEndpoint,
			region: environment.appSyncConfig.region,
			defaultAuthMode: 'iam',
		},
		Events: {
			endpoint: environment.appSyncConfig.eventsEndpoint,
			region: environment.appSyncConfig.region,
			defaultAuthMode: 'iam',
		},
	},
	Auth: {
		Cognito: {
			identityPoolId: environment.cognitoSettings.identityPoolId,
			userPoolId: environment.cognitoSettings.UserPoolId,
			userPoolClientId: environment.cognitoSettings.ClientId,
			allowGuestAccess: true,
		},
	},
});

if (environment.production) {
	enableProdMode();
	if (window) window.console.log = function () {};
}

(window as any).ShadowRoot = class extends DocumentFragment {};

document.addEventListener('DOMContentLoaded', () => {
	bootstrapApplication(AppComponent, appConfig).catch(err =>
		reportAnalyticsError('[Analytics] bootstrap failed', err, {
			feature: 'analytics',
			operation: 'bootstrap',
		})
	);
});
